import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import SubjectSelector from '../../components/SubjectSelector';
import { useAuth } from '../../contexts/AuthContext';
import { CURRICULUM_OPTIONS } from '../../constants/curriculum';
import { composeLocation, parseLocation } from '../../utils/location';
import { locationApi } from '../../services/location.service';

type StudentClassType = 'ACADEMIC' | 'COMPETITIVE' | 'ACTIVITY';

interface StudentClassSubjectCombination {
  type: StudentClassType;
  className: string;
  subjectIds: string[];
  curriculum?: string;
}

interface StudentProfilePayload {
  firstName?: string;
  lastName?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  };
  dateOfBirth?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER' | 'PREFER_NOT_TO_SAY';
  currentLocation?: string;
  address?: string;
  classSubjectCombinations?: StudentClassSubjectCombination[];
  interests?: string[];
  hobbies?: string[];
}

const ALL_TYPES: StudentClassType[] = ['ACADEMIC', 'COMPETITIVE', 'ACTIVITY'];

const normalizeClassSubjectCombinations = (
  combinations: StudentProfilePayload['classSubjectCombinations'],
): StudentClassSubjectCombination[] =>
  (Array.isArray(combinations) ? combinations : [])
    .map((item) => ({
      type: item?.type as StudentClassType,
      className: String(item?.className || item?.type || '').trim(),
      subjectIds: Array.from(
        new Set((Array.isArray(item?.subjectIds) ? item.subjectIds : []).map((id) => String(id || '').trim()).filter(Boolean)),
      ),
      curriculum: String(item?.curriculum || '').trim(),
    }))
    .filter((item) => ALL_TYPES.includes(item.type) && item.className);

const normalizeStudentProfilePayload = (incoming: StudentProfilePayload): StudentProfilePayload => {
  const normalizedDob = String(incoming.dateOfBirth || '').trim();
  return {
    dateOfBirth: normalizedDob ? normalizedDob.split('T')[0] : '',
    gender: incoming.gender,
    currentLocation: incoming.currentLocation || '',
    address: incoming.address || '',
    classSubjectCombinations: normalizeClassSubjectCombinations(incoming.classSubjectCombinations),
    interests: Array.isArray(incoming.interests) ? incoming.interests : [],
    hobbies: Array.isArray(incoming.hobbies) ? incoming.hobbies : [],
  };
};

const StudentProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profile, setProfile] = useState<StudentProfilePayload>({
    dateOfBirth: '',
    gender: undefined,
    currentLocation: '',
    address: '',
    classSubjectCombinations: [],
    interests: [],
    hobbies: [],
  });
  const [interestsInput, setInterestsInput] = useState('');
  const [hobbiesInput, setHobbiesInput] = useState('');
  const [stateName, setStateName] = useState('');
  const [district, setDistrict] = useState('');
  const [mandal, setMandal] = useState('');
  const [village, setVillage] = useState('');
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);
  const [mandalOptions, setMandalOptions] = useState<string[]>([]);
  const [villageOptions, setVillageOptions] = useState<string[]>([]);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [detectMessage, setDetectMessage] = useState('');

  const usedTypes = useMemo(
    () => new Set((profile.classSubjectCombinations || []).map((item) => item.type)),
    [profile.classSubjectCombinations],
  );
  const availableTypes = useMemo(
    () => ALL_TYPES.filter((type) => !usedTypes.has(type)),
    [usedTypes],
  );

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<StudentProfilePayload>('/students/profile');
      const incoming = response.data || {};
      const normalizedProfile = normalizeStudentProfilePayload(incoming);
      setProfile(normalizedProfile);
      setFirstName(
        String(incoming.firstName || incoming.user?.firstName || user?.firstName || '').trim(),
      );
      setLastName(
        String(incoming.lastName || incoming.user?.lastName || user?.lastName || '').trim(),
      );
      const parsedLocation = parseLocation(incoming.currentLocation || '');
      setStateName(parsedLocation.state);
      setDistrict(parsedLocation.district);
      setMandal(parsedLocation.mandal);
      setVillage(parsedLocation.village);
      setInterestsInput(Array.isArray(incoming.interests) ? incoming.interests.join(', ') : '');
      setHobbiesInput(Array.isArray(incoming.hobbies) ? incoming.hobbies.join(', ') : '');
      setMessage('');
    } catch (error) {
      console.error('Error loading student profile:', error);
      setMessage('Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, [user?.firstName, user?.lastName]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const loadStates = async () => {
      setStateOptions(await locationApi.getStates());
    };
    void loadStates();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!stateName) {
        setDistrictOptions([]);
        return;
      }
      setDistrictOptions(await locationApi.getDistricts(stateName));
    };
    void loadDistricts();
  }, [stateName]);

  useEffect(() => {
    const loadMandals = async () => {
      if (!stateName || !district) {
        setMandalOptions([]);
        return;
      }
      setMandalOptions(await locationApi.getMandals(stateName, district));
    };
    void loadMandals();
  }, [district, stateName]);

  useEffect(() => {
    const loadVillages = async () => {
      if (!stateName || !district || !mandal) {
        setVillageOptions([]);
        return;
      }
      setVillageOptions(await locationApi.getVillages(stateName, district, mandal));
    };
    void loadVillages();
  }, [district, mandal, stateName]);

  useEffect(() => {
    const composedLocation = composeLocation(stateName, district, mandal, village);
    setProfile((prev) => ({
      ...prev,
      currentLocation: composedLocation,
    }));
  }, [stateName, district, mandal, village]);

  const addCombination = () => {
    if (availableTypes.length === 0) {
      setMessage('You can add only one combination for each type.');
      return;
    }
    setProfile((prev) => ({
      ...prev,
      classSubjectCombinations: [
        ...(prev.classSubjectCombinations || []),
        { type: availableTypes[0], className: availableTypes[0], subjectIds: [], curriculum: '' },
      ],
    }));
  };

  const removeCombination = (index: number) => {
    setProfile((prev) => ({
      ...prev,
      classSubjectCombinations: (prev.classSubjectCombinations || []).filter((_, idx) => idx !== index),
    }));
  };

  const updateCombination = (
    index: number,
    patch: Partial<StudentClassSubjectCombination>,
  ) => {
    setProfile((prev) => ({
      ...prev,
      classSubjectCombinations: (prev.classSubjectCombinations || []).map((item, idx) =>
        idx === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const parseCsv = (raw: string) =>
    Array.from(
      new Set(
        String(raw || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    );

  const normalizeLocationText = (value?: string) =>
    (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const matchFromOptions = (options: string[], candidates: string[]): string => {
    for (const candidate of candidates) {
      const normalizedCandidate = normalizeLocationText(candidate);
      if (!normalizedCandidate) continue;
      const direct = options.find(
        (option) => normalizeLocationText(option) === normalizedCandidate,
      );
      if (direct) return direct;
      const partial = options.find(
        (option) =>
          normalizeLocationText(option).includes(normalizedCandidate) ||
          normalizedCandidate.includes(normalizeLocationText(option)),
      );
      if (partial) return partial;
    }
    return '';
  };

  const inferHierarchyFromAddress = async (
    address: Record<string, string>,
  ): Promise<{ state: string; district: string; mandal: string; village: string }> => {
    const states = stateOptions.length ? stateOptions : await locationApi.getStates();
    const stateCandidates = [address.state, address.region, address.state_district];
    const inferredState = matchFromOptions(states, stateCandidates) || states[0] || '';

    const districts = inferredState ? await locationApi.getDistricts(inferredState) : [];
    const districtCandidates = [
      address.county,
      address.state_district,
      address.city_district,
      address.city,
      address.town,
    ];
    const inferredDistrict = matchFromOptions(districts, districtCandidates);

    const mandals =
      inferredState && inferredDistrict
        ? await locationApi.getMandals(inferredState, inferredDistrict)
        : [];
    const mandalCandidates = [
      address.suburb,
      address.city_district,
      address.village,
      address.town,
      address.municipality,
    ];
    const inferredMandal = matchFromOptions(mandals, mandalCandidates);

    const villages =
      inferredState && inferredDistrict && inferredMandal
        ? await locationApi.getVillages(inferredState, inferredDistrict, inferredMandal)
        : [];
    const villageCandidates = [
      address.village,
      address.suburb,
      address.hamlet,
      address.neighbourhood,
      address.town,
      address.city,
    ];
    const inferredVillage = matchFromOptions(villages, villageCandidates);

    return {
      state: inferredState,
      district: inferredDistrict,
      mandal: inferredMandal,
      village: inferredVillage,
    };
  };

  const validateProfile = (): string | null => {
    if (!profile.dateOfBirth) return 'Date of birth is required.';
    if (!profile.gender) return 'Gender is required.';
    if (!String(profile.currentLocation || '').trim()) return 'Current location is required.';
    if (!String(profile.address || '').trim()) return 'Address is required.';
    if (!Array.isArray(profile.classSubjectCombinations) || profile.classSubjectCombinations.length === 0) {
      return 'Add at least one class and subject combination.';
    }

    const typeSet = new Set<string>();
    for (const item of profile.classSubjectCombinations) {
      if (!item.type || !ALL_TYPES.includes(item.type)) return 'Invalid class type selected.';
      if (typeSet.has(item.type)) return 'Each class type can be selected only once.';
      typeSet.add(item.type);
      if (!Array.isArray(item.subjectIds) || item.subjectIds.length === 0) {
        return `Select at least one subject for ${item.type}.`;
      }
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateProfile();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload: StudentProfilePayload = {
        dateOfBirth: profile.dateOfBirth ? `${profile.dateOfBirth}T00:00:00.000Z` : undefined,
        gender: profile.gender,
        currentLocation: String(profile.currentLocation || '').trim(),
        address: String(profile.address || '').trim(),
        classSubjectCombinations: (profile.classSubjectCombinations || []).map((item) => ({
          type: item.type,
          className: String(item.className || item.type || '').trim(),
          subjectIds: item.subjectIds,
          curriculum: String(item.curriculum || '').trim() || undefined,
        })),
        interests: parseCsv(interestsInput),
        hobbies: parseCsv(hobbiesInput),
      };
      const response = await api.patch<StudentProfilePayload>('/students/profile', payload);
      const updated = response.data || payload;
      setProfile(normalizeStudentProfilePayload(updated));
      setFirstName(
        String(updated.firstName || updated.user?.firstName || user?.firstName || firstName || '').trim(),
      );
      setLastName(
        String(updated.lastName || updated.user?.lastName || user?.lastName || lastName || '').trim(),
      );
      setMessage('Profile updated successfully.');
    } catch (error: any) {
      console.error('Error saving student profile:', error);
      const text = error?.response?.data?.message?.message || error?.response?.data?.message || 'Failed to save profile.';
      setMessage(typeof text === 'string' ? text : 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleDetectLocation = async () => {
    if (!navigator.geolocation) {
      setMessage('Geolocation is not supported by your browser.');
      return;
    }
    try {
      setIsDetectingLocation(true);
      setDetectMessage('');
      const coords = await new Promise<{ lat: number; lng: number }>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) =>
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }),
          reject,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        );
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1`,
      );
      const data = await response.json();
      const inferred = await inferHierarchyFromAddress(data?.address || {});

      if (inferred.state) setStateName(inferred.state);
      if (inferred.district) setDistrict(inferred.district);
      if (inferred.mandal) setMandal(inferred.mandal);
      if (inferred.village) setVillage(inferred.village);

      if (inferred.state && inferred.district && inferred.mandal && inferred.village) {
        setDetectMessage('Location detected and dropdowns were auto-filled.');
      } else {
        setDetectMessage('Location detected partially. Please complete remaining dropdown fields manually.');
      }
    } catch (error) {
      console.error('Failed to detect location:', error);
      setDetectMessage('Could not detect location. Please select manually.');
    } finally {
      setIsDetectingLocation(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_28%),linear-gradient(135deg,_#020617_0%,_#0f172a_56%,_#1d4ed8_100%)] px-6 py-6 text-white shadow-[0_30px_90px_-48px_rgba(15,23,42,0.55)] sm:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-100">Student Identity</p>
            <h3 className="mt-2 text-3xl font-semibold tracking-tight">Student Profile</h3>
            <p className="mt-2 max-w-2xl text-sm text-slate-200">
              Complete your learning profile so teachers and the platform can match you with the right nearby classes and subjects.
            </p>
          </div>
          <Link
            to="/student/dashboard"
            className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
          >
            Back To Dashboard
          </Link>
        </div>
      </section>

      <div className="space-y-6">
        {message && (
          <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
            {message}
          </div>
        )}

        {loading ? (
          <div className="rounded-[1.75rem] border border-slate-200 bg-white px-6 py-10 text-sm text-slate-600 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
            Loading profile...
          </div>
        ) : (
          <>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(280px,0.95fr)]">
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Student Record</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">Identity snapshot</p>
                <p className="mt-1 text-sm text-slate-500">Your account names are shown here as registered.</p>
                <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  readOnly
                      className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-700"
                />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  readOnly
                      className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-700"
                />
                  </div>
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-sky-200 bg-sky-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Discovery Readiness</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">Location and subject matching</p>
                <p className="mt-2 text-sm text-sky-800">
                  Complete DOB, gender, location, address, and at least one class-subject combination to improve teacher matching.
                </p>
                <div className="mt-5 space-y-3 text-sm text-slate-700">
                  <div className="rounded-2xl bg-white/80 px-4 py-3">
                    Current combinations: {(profile.classSubjectCombinations || []).length}
                  </div>
                  <div className="rounded-2xl bg-white/80 px-4 py-3">
                    Interests saved: {parseCsv(interestsInput).length}
                  </div>
                  <div className="rounded-2xl bg-white/80 px-4 py-3">
                    Hobbies saved: {parseCsv(hobbiesInput).length}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Personal Details</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">Basic profile information</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Date of Birth</label>
                <input
                  type="date"
                  value={profile.dateOfBirth || ''}
                  onChange={(e) => setProfile((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
                    className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                />
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-slate-700">Current Location</label>
                    <button
                      type="button"
                      onClick={handleDetectLocation}
                      disabled={isDetectingLocation}
                        className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 transition hover:bg-sky-100 disabled:opacity-60"
                    >
                      {isDetectingLocation ? 'Detecting...' : 'Detect'}
                    </button>
                  </div>
                  {detectMessage && <p className="mt-1 text-xs text-indigo-700">{detectMessage}</p>}
                  <div className="mt-1 grid grid-cols-1 gap-3 md:grid-cols-4">
                    <select
                      value={stateName}
                      onChange={(e) => {
                        setStateName(e.target.value);
                        setDistrict('');
                        setMandal('');
                        setVillage('');
                      }}
                        className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    >
                      <option value="">Select state</option>
                      {stateOptions.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                    <select
                      value={district}
                      onChange={(e) => {
                        setDistrict(e.target.value);
                        setMandal('');
                        setVillage('');
                      }}
                      disabled={!stateName}
                        className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white disabled:bg-slate-100"
                    >
                      <option value="">Select district</option>
                      {districtOptions.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                    <select
                      value={mandal}
                      onChange={(e) => {
                        setMandal(e.target.value);
                        setVillage('');
                      }}
                      disabled={!stateName || !district}
                        className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white disabled:bg-slate-100"
                    >
                      <option value="">Select mandal</option>
                      {mandalOptions.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                    <select
                      value={village}
                      onChange={(e) => setVillage(e.target.value)}
                      disabled={!stateName || !district || !mandal}
                        className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white disabled:bg-slate-100"
                    >
                      <option value="">Select village</option>
                      {villageOptions.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Gender</label>
                <select
                  value={profile.gender || ''}
                  onChange={(e) =>
                    setProfile((prev) => ({
                      ...prev,
                      gender: (e.target.value || undefined) as StudentProfilePayload['gender'],
                    }))
                  }
                    className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                >
                  <option value="">Select gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                  <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                </select>
                </div>
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Address</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">Full residential address</p>
              </div>
              <label className="block text-sm font-medium text-slate-700">Address</label>
              <textarea
                rows={3}
                value={profile.address || ''}
                onChange={(e) => setProfile((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Full address"
                className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
              />
            </div>

            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Learning Preferences</p>
                  <h4 className="mt-2 text-lg font-semibold text-slate-950">Class and subject combinations</h4>
                </div>
                <button
                  type="button"
                  onClick={addCombination}
                  className="inline-flex items-center rounded-full bg-slate-950 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                >
                  Add Combination
                </button>
              </div>

              {(profile.classSubjectCombinations || []).length === 0 && (
                <p className="mt-4 text-sm text-slate-500">No combinations added yet.</p>
              )}

              {(profile.classSubjectCombinations || []).map((item, index) => {
                const typesUsedByOthers = new Set(
                  (profile.classSubjectCombinations || [])
                    .filter((_, idx) => idx !== index)
                    .map((entry) => entry.type),
                );

                return (
                  <div key={`${item.type}-${index}`} className="mt-4 space-y-3 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="max-w-xs">
                        <label className="block text-xs font-medium text-slate-700">Class Type</label>
                        <select
                          value={item.type}
                          onChange={(e) =>
                            updateCombination(index, {
                              type: e.target.value as StudentClassType,
                              className: e.target.value as StudentClassType,
                              subjectIds: [],
                            })
                          }
                          className="mt-2 block w-full rounded-2xl border border-slate-200 bg-white py-2.5 px-3 text-sm text-slate-900"
                        >
                          {ALL_TYPES.filter((type) => !typesUsedByOthers.has(type) || type === item.type).map(
                            (type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                      <div className="max-w-xs">
                        <label className="block text-xs font-medium text-slate-700">Curriculum / Board</label>
                        <select
                          value={item.curriculum || ''}
                          onChange={(e) =>
                            updateCombination(index, {
                              curriculum: e.target.value,
                            })
                          }
                          className="mt-2 block w-full rounded-2xl border border-slate-200 bg-white py-2.5 px-3 text-sm text-slate-900"
                        >
                          <option value="">Select curriculum</option>
                          {CURRICULUM_OPTIONS.map((curriculum) => (
                            <option key={curriculum} value={curriculum}>
                              {curriculum}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-700">Subjects</label>
                      <SubjectSelector
                        mode="multiple"
                        filterType={item.type}
                        value={item.subjectIds}
                        onChange={(value) =>
                          updateCombination(index, {
                            subjectIds: Array.isArray(value) ? value : value ? [value] : [],
                          })
                        }
                        placeholder={`Select ${item.type.toLowerCase()} subjects`}
                        className="w-full"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeCombination(index)}
                        className="inline-flex items-center rounded-full bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Personality</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">Interests and hobbies</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Interests</label>
                <input
                  type="text"
                  value={interestsInput}
                  onChange={(e) => setInterestsInput(e.target.value)}
                  placeholder="Comma separated (e.g. Science, Coding)"
                    className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Hobbies</label>
                <input
                  type="text"
                  value={hobbiesInput}
                  onChange={(e) => setHobbiesInput(e.target.value)}
                  placeholder="Comma separated (e.g. Chess, Drawing)"
                    className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                />
                </div>
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  disabled={saving}
                  className="inline-flex items-center rounded-full bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentProfile;
